import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Sidebar from '../components/Sidebar';
import './QuotationStyles.css';

const API = 'https://cctvapi.shopkwality.in/api';

const normalizeItems = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map(i => ({
    ...i,
    gst_percent: i.gst_percent === undefined || i.gst_percent === null ? 18 : i.gst_percent
  }));
};

const EditQuotation = () => {
  const { id } = useParams();
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  const [quotation, setQuotation] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');
  const [items, setItems] = useState([]);
  const [charges, setCharges] = useState('0');
  const [discount, setDiscount] = useState('0');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadQuotation();
  }, [id]);

  // Handle items returned from BrowseProducts
  useEffect(() => {
    if (location.state) {
      if (location.state.customerName !== undefined) {
        setCustomerName(location.state.customerName);
      }
      if (location.state.customerMobile !== undefined) {
        setCustomerMobile(location.state.customerMobile);
      }
      if (location.state.charges !== undefined) {
        setCharges(location.state.charges);
      }
      if (location.state.discount !== undefined) {
        setDiscount(location.state.discount);
      }
      if (location.state.items) {
        setItems(normalizeItems(location.state.items));
      }
    }
  }, [location.state]);

  const loadQuotation = async () => {
    try {
      const res = await axios.get(`${API}/quotations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const q = res.data.data || res.data;
      setQuotation(q);
      
      // Only set initial values if not coming from BrowseProducts
      if (!location.state) {
        setCustomerName(q.customer?.name || q.customer_name || '');
        setCustomerMobile(q.customer?.mobile || q.customer_mobile || '');
        setCharges(String(q.charges || 0));
        setDiscount(String(q.discount || 0));
        
        const safeItems = Array.isArray(q?.items) ? q.items : [];
        setItems(normalizeItems(safeItems.map(i => ({
          product_id: i.product_id ?? null,
          name: i.product_name ?? '',
          quantity: Number(i.quantity ?? 0),
          unit_price: Number(i.unit_price ?? 0),
          gst_percent: i.gst_percent ?? 18
        }))));
      }
    } catch (e) {
      console.error(e);
      alert('Failed to load quotation');
      navigate('/quotations');
    } finally {
      setLoading(false);
    }
  };

  const updateItem = (idx, key, value) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      
      if (key === 'name') {
        return { ...item, name: value };
      }
      
      if (key === 'gst_percent') {
        return { ...item, gst_percent: Math.min(28, Math.max(0, Number(value) || 0)) };
      }
      
      return { ...item, [key]: Math.max(0, Number(value) || 0) };
    }));
  };

  const removeItem = (idx) => {
    if (window.confirm('Remove this item?')) {
      setItems(prev => prev.filter((_, i) => i !== idx));
    }
  };

  const addCustomProduct = () => {
    setItems(prev => [...prev, {
      product_id: null,
      name: '',
      quantity: 1,
      unit_price: 0,
      gst_percent: 18
    }]);
  };

  const subtotal = items.reduce((s, i) => s + i.quantity * i.unit_price, 0);
  const totalGst = items.reduce((s, i) => {
    const base = i.quantity * i.unit_price;
    return s + (base * (i.gst_percent ?? 18)) / 100;
  }, 0);
  const total = subtotal + totalGst + Number(charges || 0) - Number(discount || 0);

  const saveQuotation = async (andShare = false) => {
    if (!customerName || !customerMobile) {
      alert('Customer name & mobile required');
      return;
    }

    if (items.length === 0) {
      alert('Quotation must have items');
      return;
    }

    for (const i of items) {
      if (!i.product_id && !i.name) {
        alert('Custom product name required');
        return;
      }
    }

    setSaving(true);
    try {
      await axios.patch(
        `${API}/quotations/${id}`,
        {
          items: items.map(i => ({
            product_id: i.product_id,
            product_name: i.name,
            quantity: i.quantity,
            unit_price: i.unit_price,
            gst_percent: i.gst_percent
          })),
          charges: Number(charges),
          discount: Number(discount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (andShare) {
        await axios.post(
          `${API}/quotations/${id}/share`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      alert('Quotation updated successfully');
      navigate(`/quotations/${id}`);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <div className="dashboard-main">
          <div className="loading-container">
            <div className="spinner large"></div>
            <p>Loading quotation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!quotation) return null;

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-main">
        <div className="page-header-quotation">
          <div className="container">
            <h1 className="page-title">Edit {quotation.quote_no}</h1>
          </div>
        </div>

        <div className="container">
          <form onSubmit={(e) => { e.preventDefault(); saveQuotation(); }}>
            {/* Customer Information */}
            <div className="quotation-section">
              <h2 className="section-title">Customer Information</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label required">Customer Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    disabled={saving}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label required">Mobile</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={customerMobile}
                    onChange={(e) => setCustomerMobile(e.target.value)}
                    disabled={saving}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Products */}
            <div className="quotation-section">
              <div className="section-header">
                <h2 className="section-title">Products</h2>
                <div className="section-actions">
                  <button
                    type="button"
                    onClick={() => navigate('/products/browse', { 
                      state: { 
                        from: 'edit-quotation',
                        quotationId: id,
                        customerName,
                        customerMobile,
                        charges,
                        discount,
                        items
                      } 
                    })}
                    className="btn btn-secondary"
                    disabled={saving}
                  >
                    🗂️ Browse Products
                  </button>
                  <button
                    type="button"
                    onClick={addCustomProduct}
                    className="btn btn-success"
                    disabled={saving}
                  >
                    ➕ Add Custom
                  </button>
                </div>
              </div>

              {items.length === 0 ? (
                <div className="empty-state-inline">
                  <div className="empty-text">No products in quotation</div>
                  <div className="empty-subtext">Add products to continue</div>
                </div>
              ) : (
                <div className="quotation-items">
                  {items.map((item, idx) => {
                    const base = item.quantity * item.unit_price;
                    const gst = (base * (item.gst_percent ?? 18)) / 100;
                    const lineTotal = base + gst;

                    return (
                      <div key={idx} className="quotation-item-card">
                        <div className="item-header">
                          {item.product_id ? (
                            <h4>{item.name}</h4>
                          ) : (
                            <input
                              type="text"
                              className="form-input"
                              placeholder="Product name *"
                              value={item.name}
                              onChange={(e) => updateItem(idx, 'name', e.target.value)}
                              disabled={saving}
                              required
                            />
                          )}
                          <button
                            type="button"
                            onClick={() => removeItem(idx)}
                            className="btn-remove"
                            disabled={saving}
                          >
                            ✕
                          </button>
                        </div>

                        <div className="item-grid">
                          <div className="form-group">
                            <label className="form-label">Quantity</label>
                            <input
                              type="number"
                              className="form-input"
                              value={item.quantity}
                              onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                              min="0"
                              step="1"
                              disabled={saving}
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Unit Price</label>
                            <input
                              type="number"
                              className="form-input"
                              value={item.unit_price}
                              onChange={(e) => updateItem(idx, 'unit_price', e.target.value)}
                              min="0"
                              step="0.01"
                              disabled={saving}
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">GST %</label>
                            <input
                              type="number"
                              className="form-input"
                              value={item.gst_percent ?? 18}
                              onChange={(e) => updateItem(idx, 'gst_percent', e.target.value)}
                              min="0"
                              max="28"
                              disabled={saving}
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Line Total</label>
                            <div className="line-total-display">
                              ₹{lineTotal.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Additional Charges */}
            <div className="quotation-section">
              <h2 className="section-title">Additional Charges & Discount</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Additional Charges</label>
                  <input
                    type="number"
                    className="form-input"
                    value={charges}
                    onChange={(e) => setCharges(e.target.value)}
                    min="0"
                    step="0.01"
                    disabled={saving}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Discount</label>
                  <input
                    type="number"
                    className="form-input"
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                    min="0"
                    step="0.01"
                    disabled={saving}
                  />
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="quotation-summary">
              <div className="summary-row">
                <span>Subtotal:</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span>GST:</span>
                <span>₹{totalGst.toFixed(2)}</span>
              </div>
              <div className="summary-row summary-total">
                <span>Total:</span>
                <span>₹{Math.round(total)}</span>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="button"
                onClick={() => saveQuotation(true)}
                disabled={saving} 
                className="btn btn-success"
              >
                {saving ? (
                  <>
                    <span className="spinner"></span>
                    Saving...
                  </>
                ) : (
                  'Save & Share'
                )}
              </button>
              <button 
                type="submit"
                disabled={saving} 
                className="btn btn-primary"
              >
                {saving ? (
                  <>
                    <span className="spinner"></span>
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
              <button 
                type="button" 
                onClick={() => navigate(`/quotations/${id}`)} 
                className="btn btn-secondary"
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditQuotation;
